import React, { useEffect, useMemo } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  Skeleton,
  Spinner,
  Text,
  useDisclosure,
  useOutsideClick,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { SearchProviderIcon } from '../../styles/custom-icons';
import { useUtilities } from './tariffApi';

type RetailerSelectParams = {
  selectedRetailerId: number | null;
  selectedRetailerName: string;
  postCode: string;
  onSelect: (retailerId: number, planName: string, state: string) => void;
};

export const RetailerSelect = ({
  selectedRetailerId,
  selectedRetailerName,
  postCode,
  onSelect,
}: RetailerSelectParams) => {
  const { utilities, isLoading, isFetching, error: utilitiesError } = useUtilities();

  const isDisabled = isLoading;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const toast = useToast({ duration: 2000, isClosable: true });

  const EMPTY_RETAILER_OPTION = { eid: 0, name: t('Common.other'), state: '' };
  const retailerOptions = useMemo(() => {
    return [...utilities, EMPTY_RETAILER_OPTION];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilities, EMPTY_RETAILER_OPTION, postCode]);

  const retailerName = useMemo(() => {
    if (isLoading || isFetching) return `${t('Common.loading')}...`;
    if (!postCode) return t('Common.zipcode required');
    if (selectedRetailerName) return selectedRetailerName;
    return `${t('Energy Rates.find your energy provider')}...`;
  }, [isLoading, isFetching, t, postCode, selectedRetailerName]);

  const ref = React.useRef<HTMLDivElement | null>(null);

  useOutsideClick({
    ref: ref as React.RefObject<HTMLDivElement>,
    handler: onClose,
  });

  const getDropdownContents = () => {
    if (isLoading || isFetching) {
      return (
        <VStack>
          {Array.from({ length: 5 }, (_, i) => i).map((index) => (
            <Skeleton w={'100%'} h={10} rounded={2} key={`skeleton-${index}`} />
          ))}
        </VStack>
      );
    }

    return retailerOptions.map(({ eid: id, name, state }, index) => {
      const isSelected = id === selectedRetailerId;
      return (
        <Button
          variant="ghost"
          w="100%"
          rounded={0}
          fontWeight={400}
          justifyContent={'flex-start'}
          key={`${name}-${id}`}
          onClick={() => {
            onSelect(id, name, state);
            onClose();
          }}
          py={6}
          color={isSelected ? 'white' : undefined}
          bg={isSelected ? 'customGrey.400' : undefined}
          _dark={{ bg: isSelected ? 'customGrey.500' : undefined }}
          data-testid={`retailer-option-${index}`}
        >
          <Text overflow="hidden" textOverflow="ellipsis">
            {name}
          </Text>
        </Button>
      );
    });
  };

  useEffect(() => {
    if (!isLoading && utilitiesError && !toast.isActive('utilities-fetch-error')) {
      toast({
        title: t('Energy Rates.error fetching retailers'),
        description: `${t('Common.please try again')} ${t('Energy Rates.if error persists fallback to manual')}`,
        status: 'error',
        id: 'utilities-fetch-error',
      });
    }
  }, [isLoading, t, toast, utilitiesError]);

  return (
    <Box position={'relative'} ref={ref}>
      <HStack
        w="100%"
        justify={'space-between'}
        px={4}
        py={3}
        color={isDisabled ? 'customGrey.400' : undefined}
        onClick={() => {
          if (isDisabled) return;
          if (isOpen) {
            onClose();
          } else {
            onOpen();
          }
        }}
        borderColor={isOpen ? 'primaryBranding.200' : 'customGrey.300'}
        _dark={{ borderColor: isOpen ? 'primaryBranding.200' : 'customGrey.400' }}
        border="1px solid"
        data-testid="retailer-select-value"
      >
        <HStack spacing={2} minW={0}>
          <SearchProviderIcon boxSize={6} color="customGrey.400" flexShrink={0} />
          <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {retailerName}
          </Text>
        </HStack>
        <Box>{isFetching ? <Spinner size="sm" /> : <ChevronDownIcon w={6} h={6} color="#A6A6A6" />}</Box>
      </HStack>

      {isOpen && (
        <Box
          zIndex={2000}
          position="absolute"
          top="100%"
          left={0}
          w="100%"
          bg={'#ffffff'}
          _dark={{ bg: '#111111' }}
          borderLeft="1px solid"
          borderRight="1px solid"
          borderBottom="1px solid"
          borderColor="primaryBranding.200"
          data-testid="retailer-select-dropdown"
        >
          <Box>{getDropdownContents()}</Box>
        </Box>
      )}
    </Box>
  );
};
